import {
  useCreateSuperAdminClient,
  useSuperAdminClients,
} from "@babackapp/private-api-client/client/use-super-admin-clients";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminPageShell } from "./admin-tab-pages";
import { MAX_LENGTH } from "./form-values";
import {
  CLIENT_IMPORTANCE_LABELS,
  CLIENT_RELATION_LABELS,
  CLIENT_STATUS_LABELS,
  type ClientImportance,
  type ClientRelationHealth,
  type ClientStatus,
} from "./shop-shared";

export function formatEuros(amount: number): string {
  return amount.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    // Prices like 49.90 € are not rounded: this is what the client pays.
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function LabelBadge({
  entry,
}: {
  entry: { label: string; color: string } | undefined;
}) {
  if (!entry) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${entry.color}`}
    >
      {entry.label}
    </span>
  );
}

/** Baback clients and the shops grouped under them (BBS-406). */
export function AdminClientsPage() {
  const navigate = useNavigate();
  const { data: clients, isPending, isError } = useSuperAdminClients();
  const createMutation = useCreateSuperAdminClient();
  const [search, setSearch] = useState("");
  const [newClientName, setNewClientName] = useState("");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return clients ?? [];
    return (clients ?? []).filter(
      ({ name, csm, shops }) =>
        name.toLowerCase().includes(query) ||
        csm?.toLowerCase().includes(query) ||
        shops.some((shop) => shop.name.toLowerCase().includes(query)),
    );
  }, [clients, search]);

  function handleCreate() {
    // A failure shows under the header, via `isError`.
    createMutation.mutate(
      { body: { name: newClientName.trim() } },
      {
        onSuccess: (client) => {
          setNewClientName("");
          navigate(`/admin/clients/${client.id}`);
        },
      },
    );
  }

  return (
    <AdminPageShell
      title="Clients"
      description="Un client regroupe les boutiques d'une même entreprise. Le super admin fait foi pour la relation client."
      actions={
        <div className="flex items-center gap-2">
          <Input
            value={newClientName}
            onChange={(event) => setNewClientName(event.target.value)}
            placeholder="Nom du nouveau client"
            maxLength={MAX_LENGTH.name}
            className="h-8 w-56"
          />
          <Button
            size="sm"
            disabled={!newClientName.trim() || createMutation.isPending}
            onClick={handleCreate}
          >
            Créer
          </Button>
        </div>
      }
    >
      {createMutation.isError && (
        <p className="text-sm text-destructive">
          La création du client a échoué.
        </p>
      )}
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Rechercher un client, un CSM ou une boutique…"
        className="max-w-md bg-background"
      />

      {isPending ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">
          Impossible de charger les clients.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {clients?.length
            ? "Aucun client ne correspond."
            : "Aucun client pour l'instant : créez-en un, ou rattachez des boutiques depuis la liste des boutiques ou leur page Configuration."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-background">
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Client</th>
                <th className="px-4 py-2 font-medium">Statut</th>
                <th className="px-4 py-2 font-medium">CSM</th>
                <th className="px-4 py-2 font-medium">Importance</th>
                <th className="px-4 py-2 font-medium">Relation</th>
                <th className="px-4 py-2 font-medium">Boutiques</th>
                <th className="px-4 py-2 text-right font-medium">
                  Abonnements
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((client) => (
                <tr key={client.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/clients/${client.id}`}
                      className="font-medium text-violet-700 hover:underline"
                    >
                      {client.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <LabelBadge
                      entry={
                        CLIENT_STATUS_LABELS[client.status as ClientStatus]
                      }
                    />
                  </td>
                  <td className="px-4 py-2">{client.csm ?? "—"}</td>
                  <td className="px-4 py-2">
                    <LabelBadge
                      entry={
                        CLIENT_IMPORTANCE_LABELS[
                          client.importance as ClientImportance
                        ]
                      }
                    />
                  </td>
                  <td className="px-4 py-2">
                    <LabelBadge
                      entry={
                        CLIENT_RELATION_LABELS[
                          client.relationHealth as ClientRelationHealth
                        ]
                      }
                    />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {client.shops.map((shop) => (
                        <Link
                          key={shop.id}
                          to={`/admin/shops/${shop.id}/configuration`}
                          className="rounded-full border px-2 py-0.5 text-xs hover:bg-muted"
                        >
                          {shop.name}
                        </Link>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatEuros(client.monthlyPriceTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminPageShell>
  );
}
